import { useSelector } from 'react-redux';

import { ViewportType } from 'clipsal-cortex-utils/src/calculations/viewport-helpers';
import { useViewportType } from 'clipsal-cortex-utils/src/hooks/use-viewport-type';

import { SiteData, SiteDataToSave, SiteDataToUpdate } from '../../api/api-site';
import { StepProgressData } from '../../api/api-step-progress';
import { baseApi } from '../../app/baseApi';
import { DEFAULT_SITE_DATA } from '../site/siteSlice';
import { selectSiteId } from './siteSlice';

export const siteApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    getSite: build.query<SiteData, number>({
      query: (siteId) => {
        return {
          url: `/fleet/sites/${siteId}`,
          method: 'GET',
        };
      },
      providesTags: (_, __, siteId) => [{ type: 'Site', siteId }],
    }),
    postSite: build.mutation<SiteData, SiteDataToSave>({
      query: (siteData) => ({
        url: '/fleet/sites',
        method: 'POST',
        body: siteData,
      }),
      invalidatesTags: ['Sites'],
    }),
    patchSite: build.mutation<SiteData, Partial<SiteDataToUpdate>>({
      query: (siteData) => ({
        url: `/fleet/sites/${siteData.clipsal_solar_id}`,
        method: 'PATCH',
        body: siteData,
      }),
      invalidatesTags: (_, __, { clipsal_solar_id: siteId }) => [{ type: 'Site', siteId }, 'Sites'],
    }),
    getSiteCommissioningProgress: build.query<StepProgressData[], number>({
      query: (siteId) => `/fleet/sites/${siteId}/commissioning_progress`,
      providesTags: (_, __, siteId) => [{ type: 'SiteCommissioningProgress', siteId }],
    }),
    getNearmapImage: build.query<{ file_type: string; url: string }, { size: ViewportType; siteId: number }>({
      query: ({ size, siteId }) => `/v1/sites/${siteId}/image_url?size=${size}`,
      providesTags: (_, __, { siteId }) => [{ type: 'NearmapImage', id: siteId }],
    }),
    refreshSiteImage: build.mutation<void, number>({
      query: (siteId) => ({
        url: `/v1/sites/${siteId}/refresh_images`,
        method: 'POST',
      }),
      invalidatesTags: (_, __, siteId) => [{ type: 'NearmapImage', id: siteId }],
    }),
  }),
});

export const {
  useGetNearmapImageQuery,
  useRefreshSiteImageMutation,
  useGetSiteQuery,
  usePostSiteMutation,
  usePatchSiteMutation,
  useGetSiteCommissioningProgressQuery,
} = siteApi;

export const useSite = (siteId?: number) => {
  const selectedSiteId = useSelector(selectSiteId);
  const response = useGetSiteQuery(siteId ?? selectedSiteId ?? 0, { skip: !siteId && !selectedSiteId });
  return { site: response.data ?? DEFAULT_SITE_DATA, ...response };
};

export const useGetSiteCommissioningProgress = (selectedSiteId: number | null) => {
  const response = useGetSiteCommissioningProgressQuery(selectedSiteId ?? 0, { skip: !selectedSiteId });
  return { progress: response.data ?? [], ...response };
};

export const useNearmapImage = (skip?: boolean) => {
  const { site } = useSite();
  const siteId = site.clipsal_solar_id;
  const { viewportType } = useViewportType();
  const { data } = useGetNearmapImageQuery({ size: viewportType, siteId }, { skip });
  return data?.url || '';
};
